import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useSchedeClienti, SchedaCliente } from '../hooks/useSchedeClienti';
import { COLORE_RIPRESE, COLORE_TESTI, COLORE_PROGRAMMARE } from '../components/ContatoriProfili';
import AndamentoMensile, { SerieMensile } from '../components/AndamentoMensile';
import { T, FONT_SERIF } from '../theme/radar';

// Pagina "Analisi" (Configurazione): prestazioni del mese e tempi medi di
// lavoro. Stile piatto del Radar: nessun riquadro colorato, sezioni separate
// da filetti, numeri tabulari, il colore sta solo nei pallini di legenda.
// I numeri per cliente vengono dallo stesso hook di "Da gestire"
// (useSchedeClienti), i tempi medi da GET /api/dashboard/tempi-medi.

// Risposta di GET /api/dashboard/tempi-medi (vedi dashboardController.getTempiMedi)
interface TempiMedi {
  da_mese: string;
  scrittura: {
    mesi: { mese: string; clienti: number; completi: number; gg_per_scrivere: number | null; mediana: number | null }[];
    media: { gg_per_scrivere: number | null; mediana: number | null; campione: number; tasso_completamento: number };
  };
  riprese: {
    mesi: { mese: string; uscite: number; clienti: number; reel: number; reel_per_uscita: number | null; in_agenda: number | null }[];
    media: { uscite_mese: number | null; uscite_per_cliente: number | null; reel_per_uscita: number | null; mesi: number };
    mese_corrente: { mese: string; in_agenda: number; per_cliente: { cliente: string; uscite: number; reel: number }[] };
    per_cliente: { dal: string | null; mese: string; clienti: { cliente: string; totale: number; mese: number }[] };
    sessione: { sessioni: number; media: number | null; mediana: number | null; media_mese_corrente: number | null; sessioni_mese_corrente: number };
  };
  calendario: {
    mesi: { mese: string; clienti: number; completi: number; campione: number; gg_primo_completo: number | null; gg_da_inizio_mese: number | null }[];
    media: { gg_primo_completo: number | null; gg_da_inizio_mese: number | null; campione: number; completi: number; clienti_mesi: number; tasso_completamento: number };
  };
  non_realizzati: {
    mesi: { mese: string; clienti: number; previsti: number; usciti: number; non_realizzati: number; percentuale: number }[];
    media: { non_realizzati_mese: number | null; percentuale: number; mesi: number };
  };
  modifiche_cliente: {
    da_mese: string;
    mesi: { mese: string; modificati: number; contenuti: number; percentuale: number }[];
    totale: number;
    media_mese: number | null;
    percentuale: number;
    per_cliente: { cliente: string; modificati: number }[];
  };
  montaggio: { campione: number; media_giorni: number | null; mediana_giorni: number | null; dal: string | null; mesi: { mese: string; giorni: number | null; campione: number }[] };
  anticipo_montaggio: { campione: number; media_giorni: number | null; mediana_giorni: number | null; in_ritardo: number; dal: string | null; mesi: { mese: string; giorni: number | null; campione: number }[] };
  registrato_programmato: { campione: number; media_giorni: number | null; mediana_giorni: number | null; dal: string | null; mesi: { mese: string; giorni: number | null; campione: number }[] };
}

// Colori delle sei fasi, gli stessi delle righe delle card di Da gestire
const FASI = [
  { key: 'daScrivere', label: 'Da scrivere', colore: '#D97706', title: 'Testi ancora da scrivere per arrivare al target del mese (0 se i testi li scrive il cliente)', valore: (s: SchedaCliente) => s.daScrivere },
  { key: 'daRegistrare', label: 'Da registrare', colore: '#7C3AED', title: 'Testi pronti ancora da girare, come nella pagina Riprese', valore: (s: SchedaCliente) => s.daRegistrare },
  { key: 'registrati', label: 'Registrati', colore: '#2563EB', title: 'Reel girati nel mese, anche se già programmati', valore: (s: SchedaCliente) => s.reelGiaGirati },
  { key: 'daMontare', label: 'Da montare', colore: '#EA580C', title: 'Registrati e non ancora montati, come nella pagina Editing', valore: (s: SchedaCliente) => s.daMontare },
  { key: 'montati', label: 'Montati', colore: '#0D9488', title: 'Video montati nel mese, anche se già programmati', valore: (s: SchedaCliente) => s.montati },
  { key: 'programmati', label: 'Programmati', colore: '#059669', title: 'Programmati o pubblicati con data nel mese', valore: (s: SchedaCliente) => s.programmati },
] as const;

const meseBreve = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  const s = new Date(y, m - 1, 1).toLocaleDateString('it-IT', { month: 'short', year: '2-digit' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const fmt = (n: number | null, dec = 1) => (n === null ? '—' : n.toLocaleString('it-IT', { maximumFractionDigits: dec }));
const giorni = (n: number | null) => (n === null ? '—' : `${fmt(n)} ${Math.abs(n) === 1 ? 'giorno' : 'giorni'}`);

/* ---------- mattoni grafici (piatti) ---------- */

const Pallino: React.FC<{ colore: string; spento?: boolean }> = ({ colore, spento }) => (
  <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: spento ? T.border : colore }} />
);

// Etichetta piccola, maiuscola, spaziata (labelSmall del Radar)
const Etichetta: React.FC<{ children: React.ReactNode; title?: string; className?: string }> = ({ children, title, className = '' }) => (
  <div
    className={`flex items-center gap-1.5 ${className}`}
    title={title}
    style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.1, textTransform: 'uppercase', color: T.inkFaint }}
  >
    {children}
  </div>
);

// Titolo di sezione in serif, con eventuale sottotitolo, sopra un filetto
const Sezione: React.FC<{ titolo: string; sotto?: React.ReactNode; children: React.ReactNode }> = ({ titolo, sotto, children }) => (
  <section className="pt-6 sm:pt-8" style={{ borderTop: `1px solid ${T.borderSoft}` }}>
    <div className="mb-4 sm:mb-5">
      <h2 className="text-lg sm:text-xl" style={{ fontFamily: FONT_SERIF, color: T.ink, letterSpacing: '-0.01em' }}>{titolo}</h2>
      {sotto && <div className="mt-1 text-xs sm:text-sm" style={{ color: T.inkMuted }}>{sotto}</div>}
    </div>
    {children}
  </section>
);

// Numero grande con etichetta sopra e note sotto
const Cifra: React.FC<{ label: React.ReactNode; valore: React.ReactNode; note?: (string | null)[]; title?: string; grande?: boolean }> = ({ label, valore, note, title, grande }) => (
  <div title={title} className="min-w-0">
    <Etichetta>{label}</Etichetta>
    <div
      className={`mt-1.5 font-semibold tabular-nums leading-none ${grande ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-[28px]'}`}
      style={{ color: T.ink, letterSpacing: '-0.02em' }}
    >
      {valore}
    </div>
    {note && note.filter(Boolean).length > 0 && (
      <div className="mt-2 space-y-0.5">
        {note.filter(Boolean).map((n) => (
          <p key={n as string} className="text-[11px] sm:text-xs leading-snug" style={{ color: T.inkMuted }}>{n}</p>
        ))}
      </div>
    )}
  </div>
);

// Barra di avanzamento sottile (2px)
const Barra: React.FC<{ pct: number; colore?: string; className?: string }> = ({ pct, colore = T.ink, className = '' }) => (
  <div className={`h-0.5 rounded-full overflow-hidden ${className}`} style={{ backgroundColor: T.borderSoft }}>
    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: colore }} />
  </div>
);

// Voce che apre/chiude un dettaglio (tabelle): freccia che ruota, testo in
// grigio finché è chiusa. Stesso aspetto per "Per cliente" e "Mese per mese".
const Toggle: React.FC<{ aperto: boolean; onClick: () => void; label: string; extra?: string; className?: string }> = ({ aperto, onClick, label, extra, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={aperto}
    className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${className}`}
    style={{ color: aperto ? T.ink : T.inkMuted }}
  >
    <ChevronRight size={16} strokeWidth={1.75} className="transition-transform" style={{ transform: aperto ? 'rotate(90deg)' : 'none' }} />
    {label}
    {extra && <span className="font-normal" style={{ color: T.inkFaint }}>· {extra}</span>}
  </button>
);

const th = 'text-right font-semibold px-3 py-2.5 whitespace-nowrap';
const thStyle: React.CSSProperties = { fontSize: 10.5, letterSpacing: 1.1, textTransform: 'uppercase', color: T.inkFaint };

/* ---------- pagina ---------- */

const PrestazioniMese: React.FC = () => {
  const { state } = useData();
  const { schede, alertDataPronti, totali } = useSchedeClienti();
  const [mostraClienti, setMostraClienti] = useState(false);
  const [mostraMesi, setMostraMesi] = useState(false);
  // Passaggi con l'andamento aperto (un toggle per passaggio)
  const [andamentoAperto, setAndamentoAperto] = useState<Set<string>>(new Set());

  // Tempi medi: solo admin/publisher (403 per gli altri → sezione nascosta)
  const [tempi, setTempi] = useState<TempiMedi | null>(null);
  useEffect(() => {
    fetch('/api/dashboard/tempi-medi', {
      headers: { Authorization: `Bearer ${localStorage.getItem('bymarke_token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j?.success) setTempi(j.data); })
      .catch(() => {});
  }, []);

  const stats = state.dashboardStats;
  const now = new Date();
  const clientiAttivi = stats ? stats.active_clients : schede.length;
  const reelDaRealizzare = stats ? stats.monthly_reels_target : totali.reelMensili;
  const reelRealizzati = stats ? stats.reels_completed : totali.programmati;
  const percentuale = reelDaRealizzare > 0 ? Math.round((reelRealizzati / reelDaRealizzare) * 100) : 0;
  // Mancanti cliente per cliente (come "Non realizzati"): i reel in più di un
  // cliente non coprono quelli di un altro (14/10/2026: 114 invece di 121,
  // Avvocato Lattanzio 13/8 copriva i buchi degli altri).
  const mancanti = schede.reduce((t, s) => t + s.mancantiAlTarget, 0);
  const meseLabel = now.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  const meseCap = meseLabel.charAt(0).toUpperCase() + meseLabel.slice(1);

  return (
    <div className="p-4 sm:p-6 max-w-6xl space-y-6 sm:space-y-8">
      {/* Intestazione */}
      <div>
        <h1 className="text-2xl sm:text-3xl" style={{ fontFamily: FONT_SERIF, color: T.ink, letterSpacing: '-0.01em' }}>Analisi</h1>
        <p className="mt-1 text-xs sm:text-sm" style={{ color: T.inkMuted }}>Prestazioni di {meseCap} e tempi medi di lavoro</p>
      </div>

      {/* Il mese: una cifra sola e una barra */}
      <section>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <Cifra
            grande
            label={`${meseCap} · target programmato`}
            valore={<>{percentuale}<span className="text-xl sm:text-2xl ml-0.5" style={{ color: T.inkFaint }}>%</span></>}
            note={[`${reelRealizzati} reel programmati su ${reelDaRealizzare} previsti`]}
          />
          <div className="flex gap-8 sm:gap-10">
            <Cifra label="Clienti attivi" valore={clientiAttivi} />
            <Cifra label="Reel previsti" valore={reelDaRealizzare} />
            <Cifra label="Mancanti" valore={mancanti} title="Somma dei reel che mancano a ogni cliente per arrivare al suo target: chi ne ha programmati in più non copre gli altri" />
          </div>
        </div>
        <Barra pct={percentuale} className="mt-4" />
      </section>

      {/* Flusso di lavoro: sei cifre in fila; sotto, il dettaglio per cliente
          chiuso di default (toggle), così la pagina resta corta */}
      <Sezione titolo="Flusso di lavoro" sotto="Somma delle sei righe delle card di Da gestire, tutti i clienti attivi">
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-5">
          {FASI.map((f) => (
            <Cifra
              key={f.key}
              title={f.title}
              label={<><Pallino colore={f.colore} /> {f.label}</>}
              valore={totali[f.key]}
            />
          ))}
        </div>
        <Toggle className="mt-5" aperto={mostraClienti} onClick={() => setMostraClienti((v) => !v)} label="Per cliente" extra={`${schede.length} clienti attivi`} />
        {mostraClienti && (
          <div className="mt-3">
            <TabellaClienti schede={schede} totali={totali} alertDataPronti={alertDataPronti} />
            <p className="mt-2 text-xs" style={{ color: T.inkFaint }}>Una riga per card di Da gestire · clicca una riga per aprire il cliente nel Copywriting</p>
          </div>
        )}
      </Sezione>

      {/* Tempi medi di lavoro */}
      {tempi && (() => {
        const cal = tempi.calendario.media;
        const nr = tempi.non_realizzati.media;
        const mod = tempi.modifiche_cliente;
        const mon = tempi.montaggio;
        const ant = tempi.anticipo_montaggio;
        const mesi = Array.from(new Set([
          ...tempi.scrittura.mesi.map(m => m.mese),
          ...tempi.riprese.mesi.map(m => m.mese),
          ...tempi.calendario.mesi.map(m => m.mese),
          ...tempi.non_realizzati.mesi.map(m => m.mese),
          ...tempi.modifiche_cliente.mesi.map(m => m.mese),
        ])).sort();
        const scr = tempi.scrittura.media;
        const rip = tempi.riprese.media;
        const ripOra = tempi.riprese.mese_corrente;
        const ses = tempi.riprese.sessione;
        const unita = (u: string) => <span className="text-base ml-1" style={{ color: T.inkFaint }}>{u}</span>;

        // Il flusso di lavoro come gruppi, nell'ordine in cui si lavora: ogni
        // gruppo ha le azioni che monitoriamo, ognuna con la sua serie mensile
        // per l'andamento (stessi mesi della tabella "Mese per mese").
        const serieDi = (f: (m: string) => number | null | undefined) => mesi.map((m) => ({ mese: m, valore: f(m) ?? null }));
        // `dettaglio`: un secondo toggle sotto la misura, con un contenuto libero
        // (es. la lista "Chi modifica di più")
        type Misura = { label: string; valore: React.ReactNode; note: (string | null)[]; title: string; andamento?: SerieMensile[]; dettaglio?: { label: string; contenuto: React.ReactNode } };
        const gruppi: { titolo: string; colore: string; misure: Misura[] }[] = [
          {
            titolo: 'Script e contenuti', colore: '#D97706',
            misure: [
              {
                label: 'Testi del mese completi',
                andamento: [
                  { label: 'Testi completi', unita: 'gg', colore: '#D97706', megliSeScende: true, punti: serieDi((m) => tempi.scrittura.mesi.find(x => x.mese === m)?.gg_per_scrivere), title: 'Giorni dal primo script all\'ultimo testo pronto del target, media dei clienti a target nel mese' },
                ],
                valore: scr.gg_per_scrivere === null ? '—' : <>{fmt(scr.gg_per_scrivere)}{unita('gg')}</>,
                note: [
                  'dal primo script all\'ultimo testo pronto del target',
                  scr.mediana !== null ? `mediana ${giorni(scr.mediana)}: di solito si scrivono in blocco` : null,
                  `media sui ${scr.campione} mesi-cliente arrivati a target (${scr.tasso_completamento}% del totale)`,
                ],
                title: 'Per ogni cliente e mese (contenuti creati nel mese, bozze escluse, senza chi si scrive i testi da solo): giorni dal primo script all\'N-esimo "testo pronto", con N = reel previsti.',
              },
              {
                label: 'Modifiche del cliente sugli script',
                andamento: [
                  { label: 'Modifiche del cliente', unita: '%', colore: '#D97706', megliSeScende: true, decimali: 0, punti: serieDi((m) => { const x = tempi.modifiche_cliente.mesi.find(v => v.mese === m); return x && x.contenuti > 0 ? x.percentuale : null; }), title: 'Contenuti modificati dal cliente in percentuale dei contenuti creati nel mese' },
                ],
                dettaglio: mod.per_cliente.length > 0 ? {
                  label: 'Chi modifica di più',
                  contenuto: (
                    <ul className="space-y-1 text-sm">
                      {mod.per_cliente.map((c) => (
                        <li key={c.cliente} className="flex items-baseline justify-between gap-4" style={{ color: T.inkMuted }}>
                          <span className="truncate">{c.cliente}</span>
                          <span className="font-semibold tabular-nums" style={{ color: T.ink }}>{c.modificati}</span>
                        </li>
                      ))}
                    </ul>
                  ),
                } : undefined,
                valore: mod.media_mese === null ? '—' : <>{fmt(mod.media_mese)}{unita('al mese')}</>,
                note: [`${mod.totale} in totale, ${mod.percentuale}% dei contenuti`, `tracciate da ${meseBreve(mod.da_mese.slice(0, 7))}`],
                title: 'Contenuti modificati dal cliente nella sua dashboard, per mese.',
              },
            ],
          },
          {
            titolo: 'Riprese', colore: '#7C3AED',
            misure: [
              {
                label: 'Sessioni di riprese',
                andamento: [
                  { label: 'Sessioni al mese', colore: '#7C3AED', megliSeScende: false, decimali: 0, punti: serieDi((m) => tempi.riprese.mesi.find(x => x.mese === m)?.uscite), title: 'Sessioni di riprese nel mese, tutti i clienti (il mese in corso è parziale)' },
                ],
                dettaglio: (tempi.riprese.per_cliente?.clienti || []).length > 0 ? {
                  label: 'Chi impegna più uscite',
                  contenuto: (() => {
                    const pc = tempi.riprese.per_cliente;
                    return (
                      <div>
                        <div className="flex items-baseline justify-between gap-4 pb-1.5 mb-1" style={{ borderBottom: `1px solid ${T.borderSoft}` }}>
                          <Etichetta>Cliente</Etichetta>
                          <div className="flex gap-4">
                            <Etichetta className="w-12 justify-end">Totale</Etichetta>
                            <Etichetta className="w-16 justify-end">{meseBreve(pc.mese)}</Etichetta>
                          </div>
                        </div>
                        <ul className="space-y-1 text-sm">
                          {pc.clienti.map((c) => (
                            <li key={c.cliente} className="flex items-baseline justify-between gap-4" style={{ color: T.inkMuted }}>
                              <span className="truncate">{c.cliente}</span>
                              <span className="flex gap-4 whitespace-nowrap tabular-nums">
                                <span className="w-12 text-right font-semibold" style={{ color: T.ink }}>{c.totale}</span>
                                <span className="w-16 text-right" style={{ color: c.mese > 0 ? T.ink : T.inkFaint }}>{c.mese}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 text-[11px] leading-snug" style={{ color: T.inkFaint }}>
                          Appuntamenti con i videomaker dall'agenda iCloud{pc.dal ? `, dal ${new Date(pc.dal).toLocaleDateString('it-IT')}` : ''}, fino a oggi · call escluse
                        </p>
                      </div>
                    );
                  })(),
                } : undefined,
                valore: rip.uscite_mese === null ? '—' : <>{fmt(rip.uscite_mese)}{unita('al mese')}</>,
                note: [
                  rip.uscite_per_cliente !== null ? `${fmt(rip.uscite_per_cliente)} a cliente al mese` : null,
                  `${ses.sessioni} sessioni nei ${rip.mesi} mesi chiusi da ${meseBreve(tempi.da_mese.slice(0, 7))}`,
                  `a ${meseBreve(ripOra.mese)}, in corso: ${ses.sessioni_mese_corrente} sessioni`,
                ],
                title: 'Una sessione = un giorno in cui il videomaker ha segnato reel registrati per un cliente. Storico ricostruito dai log, contato da maggio 2026 (luglio parziale).',
              },
              {
                label: 'Reel per sessione',
                andamento: [
                  { label: 'Reel per sessione', colore: '#7C3AED', megliSeScende: false, punti: serieDi((m) => tempi.riprese.mesi.find(x => x.mese === m)?.reel_per_uscita), title: 'Reel registrati in media per ogni sessione di riprese, mese per mese' },
                ],
                valore: ses.media === null ? '—' : <>{fmt(ses.media)}{unita('reel')}</>,
                note: [
                  ses.mediana !== null ? `mediana ${fmt(ses.mediana)}, sulle stesse ${ses.sessioni} sessioni dei ${rip.mesi} mesi chiusi` : null,
                  ses.media_mese_corrente !== null ? `a ${meseBreve(ripOra.mese)}, in corso: ${fmt(ses.media_mese_corrente)} reel a sessione` : null,
                ],
                title: 'Quanti reel il videomaker segna registrati in una sessione (un cliente in un giorno): reel totali diviso sessioni, sugli stessi mesi chiusi.',
              },
            ],
          },
          {
            titolo: 'Editing', colore: '#EA580C',
            misure: [
              {
                label: 'Montaggio dopo la registrazione',
                andamento: [
                  { label: 'Montaggio', unita: 'gg', colore: '#EA580C', megliSeScende: true, punti: serieDi((m) => tempi.montaggio.mesi.find(x => x.mese === m)?.giorni), title: 'Giorni tra Registrato e Montato, per mese di registrazione (dal 15/09/2026)' },
                ],
                valore: mon.media_giorni === null ? <span style={{ color: T.inkFaint }}>in raccolta</span> : <>{fmt(mon.media_giorni)}{unita('gg')}</>,
                note: [
                  mon.campione > 0
                    ? `mediana ${giorni(mon.mediana_giorni)} su ${mon.campione} video, dal ${new Date(mon.dal as string).toLocaleDateString('it-IT')}`
                    : 'da Registrato a Montato: si misura dal 15/09/2026, con la pagina Editing',
                ],
                title: 'Tempo tra "Registrato" (Riprese) e "Montato" (Editing).',
              },
              {
                label: 'Montato prima dell\'uscita',
                andamento: [
                  { label: 'Montato prima dell\'uscita', unita: 'gg', colore: '#EA580C', megliSeScende: false, punti: serieDi((m) => tempi.anticipo_montaggio.mesi.find(x => x.mese === m)?.giorni), title: 'Giorni tra il "Montato" e la data di uscita programmata, per mese di uscita (negativo = montato in ritardo)' },
                ],
                valore: ant.media_giorni === null
                  ? <span style={{ color: T.inkFaint }}>in raccolta</span>
                  : <>{fmt(Math.abs(ant.media_giorni))}{unita(ant.media_giorni >= 0 ? 'gg prima' : 'gg dopo')}</>,
                note: [
                  ant.campione > 0
                    ? `mediana ${giorni(ant.mediana_giorni)} su ${ant.campione} video${ant.in_ritardo > 0 ? `, ${ant.in_ritardo} montati dopo la data di uscita` : ''}`
                    : 'quanti giorni prima della data programmata nel calendario del cliente il video è montato',
                  `si misura dal 15/09/2026, con la pagina Editing`,
                ],
                title: 'Giorni tra "Montato" (Editing) e la data di pubblicazione programmata nel calendario del cliente. Negativo = montato dopo la data prevista.',
              },
            ],
          },
          {
            titolo: 'Pubblicazione e calendario', colore: '#059669',
            misure: [
              {
                label: 'Calendario completo',
                andamento: [
                  { label: 'Calendario completo dopo', unita: 'gg', colore: '#059669', megliSeScende: true, punti: serieDi((m) => tempi.calendario.mesi.find(x => x.mese === m)?.gg_da_inizio_mese), title: 'Giorni dall\'inizio del mese al completamento del calendario, media dei clienti a target' },
                  { label: 'Calendari a target', unita: '%', colore: '#059669', megliSeScende: false, decimali: 0, punti: serieDi((m) => { const x = tempi.calendario.mesi.find(v => v.mese === m); return x && x.clienti > 0 ? Math.round((x.completi / x.clienti) * 100) : null; }), title: 'Quota di clienti che hanno raggiunto i reel previsti nel mese' },
                ],
                valore: cal.gg_da_inizio_mese === null ? '—' : <>{fmt(cal.gg_da_inizio_mese)}{unita('gg')}</>,
                note: [
                  cal.gg_da_inizio_mese === null ? null : cal.gg_da_inizio_mese >= 0 ? 'dopo l\'inizio del mese' : 'prima dell\'inizio del mese',
                  cal.gg_primo_completo !== null ? `${giorni(cal.gg_primo_completo)} dal primo contenuto programmato` : null,
                  `media sui ${cal.completi} mesi-cliente arrivati a target (${cal.tasso_completamento}% di ${cal.clienti_mesi})`,
                ],
                title: 'Per ogni cliente e mese: quando è stato programmato l\'N-esimo contenuto del mese, con N = reel previsti. Dalle date di programmazione, da maggio 2026.',
              },
              {
                label: 'Non realizzati',
                andamento: [
                  { label: 'Non realizzati', unita: '%', colore: '#059669', megliSeScende: true, decimali: 0, punti: serieDi((m) => tempi.non_realizzati.mesi.find(x => x.mese === m)?.percentuale), title: 'Reel previsti non usciti, in percentuale, solo mesi chiusi' },
                ],
                valore: nr.non_realizzati_mese === null ? '—' : <>{fmt(nr.non_realizzati_mese)}{unita('al mese')}</>,
                note: [`${nr.percentuale}% dei reel previsti`, `media sui ${nr.mesi} mesi chiusi da ${meseBreve(tempi.da_mese.slice(0, 7))}`],
                title: 'Per ogni mese chiuso: reel previsti dai clienti attivi meno reel usciti nel mese, dal primo mese in cui il cliente ha contenuti.',
              },
            ],
          },
        ];
        return (
          <Sezione titolo="Tempi medi di Lavoro" sotto={`Il flusso di lavoro in quattro passaggi, dallo storico dei contenuti da ${meseBreve(tempi.da_mese.slice(0, 7))} a oggi`}>
            <div className="space-y-6">
              {gruppi.map((g, i) => (
                <div key={g.titolo} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-8 gap-y-4 pt-5" style={{ borderTop: i === 0 ? undefined : `1px solid ${T.borderSoft}` }}>
                  {/* Nome del passaggio, numerato e col colore della fase */}
                  <div className="flex items-start gap-2">
                    <Pallino colore={g.colore} />
                    <div className="-mt-1">
                      <div className="text-sm font-semibold" style={{ color: T.ink }}>{g.titolo}</div>
                      <div style={{ fontSize: 10.5, letterSpacing: 1.1, textTransform: 'uppercase', color: T.inkFaint }}>passaggio {i + 1}</div>
                    </div>
                  </div>
                  {/* Le misure del passaggio; sotto ognuna il toggle "Andamento"
                      che apre il grafico mese per mese di QUELLA misura */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 content-start">
                    {g.misure.map((m) => {
                      const chiave = `${g.titolo}/${m.label}`;
                      const aperto = andamentoAperto.has(chiave);
                      return (
                        <div key={m.label}>
                          <Cifra label={m.label} valore={m.valore} note={m.note} title={m.title} />
                          {m.andamento && mesi.length > 1 && (
                            <Toggle
                              className="mt-2"
                              aperto={aperto}
                              onClick={() => setAndamentoAperto((s) => { const n = new Set(s); if (n.has(chiave)) n.delete(chiave); else n.add(chiave); return n; })}
                              label="Andamento"
                            />
                          )}
                          {m.andamento && aperto && (
                            <div className="mt-3 max-w-[340px]">
                              <AndamentoMensile serie={m.andamento} colonne={1} />
                            </div>
                          )}
                          {m.dettaglio && (() => {
                            const chiaveD = `${chiave}/dettaglio`;
                            const apertoD = andamentoAperto.has(chiaveD);
                            return (
                              <>
                                <Toggle
                                  className={aperto ? 'mt-4' : 'mt-2'}
                                  aperto={apertoD}
                                  onClick={() => setAndamentoAperto((s) => { const n = new Set(s); if (n.has(chiaveD)) n.delete(chiaveD); else n.add(chiaveD); return n; })}
                                  label={m.dettaglio.label}
                                />
                                {apertoD && <div className="mt-3 max-w-[340px]">{m.dettaglio.contenuto}</div>}
                              </>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {mesi.length > 0 && (
              <Toggle className="mt-6" aperto={mostraMesi} onClick={() => setMostraMesi((v) => !v)} label="Mese per mese" extra={`${mesi.length} mesi`} />
            )}
            {mesi.length > 0 && mostraMesi && (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderTop: `1px solid ${T.borderSoft}`, borderBottom: `1px solid ${T.borderSoft}` }}>
                      <th className="text-left font-semibold px-3 py-2.5" style={thStyle}>Mese</th>
                      <th className={th} style={thStyle} title="Giorni dal primo script all'ultimo testo pronto del target (mesi-cliente a target)">Testi completi</th>
                      <th className={th} style={thStyle} title="Modifiche del cliente sugli script">Modifiche cliente</th>
                      <th className={th} style={thStyle} title="Sessioni di riprese nel mese (reel per sessione)">Sessioni</th>
                      <th className={th} style={thStyle} title="Clienti arrivati a target sul totale">Calendari completi</th>
                      <th className={th} style={thStyle} title="Giorni dall'inizio del mese al completamento (negativo = prima del mese)">Completo dopo</th>
                      <th className={th} style={thStyle}>Non realizzati</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mesi.map((mese) => {
                      const s = tempi.scrittura.mesi.find(x => x.mese === mese);
                      const r = tempi.riprese.mesi.find(x => x.mese === mese);
                      const c = tempi.calendario.mesi.find(x => x.mese === mese);
                      const n = tempi.non_realizzati.mesi.find(x => x.mese === mese);
                      const md = tempi.modifiche_cliente.mesi.find(x => x.mese === mese);
                      return (
                        <tr key={mese} style={{ borderBottom: `1px solid ${T.borderSoft}` }}>
                          <td className="px-3 py-2.5 font-medium whitespace-nowrap" style={{ color: T.ink }}>{meseBreve(mese)}</td>
                          <td className="text-right px-3 py-2.5 tabular-nums" style={{ color: s && s.gg_per_scrivere !== null ? T.ink : T.inkFaint }}>
                            {s && s.gg_per_scrivere !== null ? <>{fmt(s.gg_per_scrivere)} gg<span style={{ color: T.inkFaint }}> · {s.completi}/{s.clienti}</span></> : '—'}
                          </td>
                          <td className="text-right px-3 py-2.5 tabular-nums" style={{ color: md ? T.ink : T.inkFaint }}>
                            {md ? <>{md.modificati}<span style={{ color: T.inkFaint }}> · {md.percentuale}%</span></> : '—'}
                          </td>
                          <td className="text-right px-3 py-2.5 tabular-nums" style={{ color: r ? T.ink : T.inkFaint }}>
                            {r ? <>{r.uscite}<span style={{ color: T.inkFaint }}> · {fmt(r.reel_per_uscita)} reel</span></> : '—'}
                          </td>
                          <td className="text-right px-3 py-2.5 tabular-nums" style={{ color: c ? T.ink : T.inkFaint }}>
                            {c ? <>{c.completi}<span style={{ color: T.inkFaint }}> / {c.clienti}</span></> : '—'}
                          </td>
                          <td className="text-right px-3 py-2.5 tabular-nums" style={{ color: c && c.gg_da_inizio_mese !== null ? T.ink : T.inkFaint }}>
                            {c && c.gg_da_inizio_mese !== null ? `${fmt(c.gg_da_inizio_mese)} gg` : '—'}
                          </td>
                          <td className="text-right px-3 py-2.5 tabular-nums" style={{ color: n ? T.ink : T.inkFaint }}>
                            {n ? <>{n.non_realizzati}<span style={{ color: T.inkFaint }}> su {n.previsti} · {n.percentuale}%</span></> : 'mese in corso'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

          </Sezione>
        );
      })()}

    </div>
  );
};

// Tabella per cliente: una riga per card di Da gestire, stesse sei colonne.
// Vive dentro "Flusso di lavoro", aperta dal toggle "Per cliente".
const TabellaClienti: React.FC<{ schede: SchedaCliente[]; totali: Record<string, number>; alertDataPronti: boolean }> = ({ schede, totali, alertDataPronti }) => {
  const navigate = useNavigate();
  return (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderTop: `1px solid ${T.borderSoft}`, borderBottom: `1px solid ${T.borderSoft}` }}>
                <th className="text-left font-semibold px-3 py-2.5" style={thStyle}>Cliente</th>
                <th className={th} style={thStyle} title="Reel previsti al mese">Previsti</th>
                {FASI.map((f) => (
                  <th key={f.key} className={th} style={thStyle} title={f.title}>
                    <span className="inline-flex items-center gap-1.5"><Pallino colore={f.colore} />{f.label}</span>
                  </th>
                ))}
                <th className="text-left font-semibold px-3 py-2.5" style={thStyle}>Avvisi</th>
              </tr>
            </thead>
            <tbody>
              {schede.map((s) => {
                const cliente: any = s.cliente;
                return (
                  <tr
                    key={s.clienteId}
                    onClick={() => navigate(`/copywriter?client=${s.clienteId}`)}
                    className="cursor-pointer transition-colors"
                    style={{ borderBottom: `1px solid ${T.borderSoft}` }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.surfaceHigh)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-[180px]">
                        <div className="w-6 h-6 rounded-full flex-shrink-0 overflow-hidden" style={{ backgroundColor: T.sunken }}>
                          {cliente.logoUrl ? (
                            <img src={cliente.logoUrl} alt={cliente.nomeAzienda} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] font-medium" style={{ color: T.inkFaint }}>
                              {cliente.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate flex items-center gap-1.5" style={{ color: T.ink }}>
                            {cliente.nomeAzienda}
                            {s.obiettivoRaggiunto && <Pallino colore={T.success} />}
                          </div>
                          <Barra pct={s.progresso} colore={s.obiettivoRaggiunto ? T.success : T.ink} className="w-24 mt-1" />
                        </div>
                      </div>
                    </td>
                    <td className="text-right px-3 py-2.5 tabular-nums" style={{ color: T.inkMuted }}>{s.reelMensili}</td>
                    {FASI.map((f) => {
                      if (f.key === 'daScrivere' && s.testiDalCliente) {
                        return <td key={f.key} className="text-right px-3 py-2.5 text-xs" style={{ color: T.inkFaint }} title="I testi li scrive il cliente">cliente</td>;
                      }
                      const n = f.valore(s);
                      return (
                        <td key={f.key} className="text-right px-3 py-2.5 tabular-nums font-medium" style={{ color: n > 0 ? T.ink : T.border }}>
                          {n}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5">
                      {/* Solo pallini, stessi colori dei contatori in alto; il testo è nel tooltip */}
                      {alertDataPronti && (
                        <div className="flex items-center gap-1.5">
                          {s.occorronoRiprese && (
                            <span className="inline-flex" title={`Richiede riprese: attesi ~${s.attesoAdOggi} reel girati entro oggi, disponibili ${s.reelGiaGirati}`}>
                              <Pallino colore={COLORE_RIPRESE} />
                            </span>
                          )}
                          {s.mancanoTesti && (
                            <span className="inline-flex" title="Senza testi: riprese fissate ma nessun testo pronto da registrare">
                              <Pallino colore={COLORE_TESTI} />
                            </span>
                          )}
                          {s.daProgrammareOra && (
                            <span className="inline-flex" title={`Da programmare: ${s.registratiNonProgrammati} reel registrati, programmane ${s.mancantiAlTarget} e il mese è completo`}>
                              <Pallino colore={COLORE_PROGRAMMARE} />
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-semibold" style={{ color: T.ink }}>
                <td className="px-3 py-2.5">Totale</td>
                <td className="text-right px-3 py-2.5 tabular-nums">{totali.reelMensili}</td>
                {FASI.map((f) => (
                  <td key={f.key} className="text-right px-3 py-2.5 tabular-nums">{totali[f.key]}</td>
                ))}
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
  );
};

export default PrestazioniMese;
